import { Controller } from "@hotwired/stimulus"

// Click-to-edit cells on the Data screen.
//
// Each cell starts as a chip (.sp-edit / a plain text span). Clicking the chip
// reveals a same-shape control (date/text <input> or a <select>) sized to fit
// the cell. On blur we PATCH /djobs/:id with djob[<field>]: <value>, then swap
// the chip back with the updated value (and recolor it --done / --todo for
// dates).
//
// We never mutate the chip's class list except for the --done/--todo flip,
// and we never invent a new save path - persistence goes through the
// existing JSON branch of djobs#update.
export default class extends Controller {
  static targets = ["chip"]
  static values = {
    djobId:  Number,
    field:   String,
    type:    String,  // "date" | "time" | "text" | "textarea" | "select"
    options: Array    // option strings, used when type === "select"
  }

  edit(event) {
    if (this._editing) return
    this._editing = true

    const chip   = this.chipTarget
    const raw    = chip.dataset.rawValue || ""

    let input
    if (this.typeValue === "select") {
      input = document.createElement("select")
      input.className = "sp-input"
      input.style.width = "100%"
      input.style.padding = "4px 8px"
      input.style.fontSize = "13px"
      this.optionsValue.forEach((opt) => {
        const option = document.createElement("option")
        option.value = opt
        option.textContent = opt === "" ? "—" : opt
        if (opt === raw) option.selected = true
        input.appendChild(option)
      })
      // A picked value should commit immediately, not wait for a second click.
      input.addEventListener("change", () => input.blur())
    } else if (this.typeValue === "textarea") {
      // Prose fields (Photog Notes): a real note runs to several lines, and a
      // one-line <input> scrolls all but the tail out of sight while typing.
      // Size the box to the content so the whole note is visible, and let Enter
      // do what Enter does in a note — start a new line, not save.
      input = document.createElement("textarea")
      input.value = raw
      input.className = "sp-input"
      input.style.width = "100%"
      input.style.padding = "4px 8px"
      input.style.fontSize = "13px"
      input.style.resize = "vertical"
      input.style.overflowY = "hidden"
      // Grown from the rendered height, not from a row count: these notes are
      // typically one long unbroken string that WRAPS, so counting "\n" sizes a
      // six-line note as one line and hides the rest behind a scrollbar.
      input.addEventListener("input", () => this._fitToContent(input))
    } else if (this.typeValue === "date") {
      input = document.createElement("input")
      input.type  = "date"
      input.value = raw
      input.className = "sp-input sp-input--date"
      input.style.width = "120px"
      input.style.padding = "4px 8px"
      input.style.fontSize = "12px"
    } else if (this.typeValue === "time") {
      // A clock time gets the native time picker: it enforces a real time, so
      // "8:3" or "meet at 8" can never be stored, and it accepts the AM/PM the
      // schedule is read in while submitting the 24h value the server parses.
      input = document.createElement("input")
      input.type  = "time"
      input.value = raw
      input.className = "sp-input sp-input--time"
      input.style.width = "110px"
      input.style.padding = "4px 8px"
      input.style.fontSize = "12px"
    } else {
      input = document.createElement("input")
      input.type  = "text"
      input.value = raw
      input.className = "sp-input"
      input.style.width = "100%"
      input.style.padding = "4px 8px"
      input.style.fontSize = "13px"
    }
    input.addEventListener("click", (e) => e.stopPropagation())
    input.addEventListener("blur", () => this.save(input))
    input.addEventListener("keydown", (e) => {
      // In a textarea Enter is a line break, not a save — a note is prose, and
      // committing on Enter would make a multi-line note impossible to write.
      // Cmd/Ctrl+Enter saves, click-away saves; Escape reverts everywhere.
      if (e.key === "Enter" && this.typeValue === "textarea") {
        if (e.metaKey || e.ctrlKey) input.blur()
        return
      }
      if (e.key === "Enter")  input.blur()
      if (e.key === "Escape") { input.value = raw; input.blur() }
    })

    chip.hidden = true
    chip.insertAdjacentElement("afterend", input)
    // After insertion — scrollHeight is 0 until the element is in the document.
    if (this.typeValue === "textarea") this._fitToContent(input)
    input.focus()
  }

  // Height the textarea to hold all of its text (capped so one enormous note
  // can't take over the schedule; past the cap it scrolls).
  _fitToContent(input) {
    input.style.height = "auto"
    input.style.height = `${Math.min(260, Math.max(48, input.scrollHeight))}px`
  }

  save(input) {
    const newValue = input.value
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content

    fetch(`/djobs/${this.djobIdValue}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": csrf || ""
      },
      body: JSON.stringify({ djob: { [this.fieldValue]: newValue } })
    })
      .then((r) => r.ok ? r.json() : Promise.reject(r.status))
      .then(()  => this.applyValue(input, newValue))
      .catch((err) => {
        console.error("Inline edit save failed:", err)
        // Still reflect what the user typed so they're not confused;
        // a refresh will show the true server state if save actually failed.
        this.applyValue(input, newValue)
      })
  }

  applyValue(input, newValue) {
    const chip = this.chipTarget
    chip.dataset.rawValue = newValue

    if (this.typeValue === "date") {
      if (newValue) {
        chip.textContent = this._shortDate(newValue)
        chip.classList.remove("sp-edit--todo")
        chip.classList.add("sp-edit--done")
      } else {
        chip.textContent = ""
        chip.classList.remove("sp-edit--done")
        chip.classList.add("sp-edit--todo")
      }
    } else if (this.typeValue === "time") {
      // Mirror what the server rendered ("8:30 AM"), not the 24h value the
      // input submits — otherwise the cell reads differently after a save than
      // it does after a reload.
      chip.textContent = newValue ? this._clockTime(newValue) : "—"
    } else {
      // Mirror the partial's initial render: blank text/select shows the dash.
      chip.textContent = newValue || "—"
    }

    chip.hidden = false
    input.remove()
    this._editing = false
  }

  // "2026-05-30" -> "05/30" (matches the prototype's dense layout)
  _shortDate(iso) {
    const [, m, d] = iso.split("-")
    return `${m}/${d}`
  }

  // "08:30" -> "8:30 AM". Mirrors djob_time_display (%l:%M %p), which is how
  // every other time on the schedule is printed.
  _clockTime(hhmm) {
    const [h, m] = hhmm.split(":").map(Number)
    if (Number.isNaN(h) || Number.isNaN(m)) return hhmm
    const suffix = h < 12 ? "AM" : "PM"
    const hour12 = h % 12 === 0 ? 12 : h % 12
    return `${hour12}:${String(m).padStart(2, "0")} ${suffix}`
  }
}
